import React, { useEffect, useState } from "react";
import { 
  Grid, TextField, FormControl, InputLabel, Select, MenuItem, 
  Button, Box, Typography 
} from "@mui/material";
import { Search, AttachFile, CheckCircle } from "@mui/icons-material"; // Added CheckCircle here
import { Link } from "react-router-dom";
import { ConsultantList, ContractorList } from "../../../../Shared/PreRenderedComponents";
import { GetDataFromApiWithParams } from "../../../../../../services/api/ExecuteApiRequests";
import ParkList from "../../../../Shared/PreRenderedComponents/ParkList";
import IPDCToastMessageResult from "../../../../../../components/Controls/IPDCToastMessageResult";

const requestTypes = ["New", "Modification", "Extension", "Maintenance", "Demolition"];
const initialSearchResult = {
  titleDeedNo: "",
  park: "",
  upin: "",
  investor: "",
  tin_no: "",
};

function GeneralInfoTab({
  requestDate,
  setRequestDate,
  requestType,
  setRequestType,
  agreementId,
  setAgreementId,
  consultant,
  setConsultant,
  contractor,
  setContractor,
  selectedPark: propSelectedPark,
  setSelectedPark: propSetSelectedPark,
  requestFormAttachment, // New prop
  setRequestFormAttachment, // New prop
}) {
  const [infoFromAgreement, setInfoFromAgreement] = useState(initialSearchResult);
  const [responseMessage, setResponseMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [agreementsList, setAgreementsList] = useState([]);
  const [localSelectedPark, setLocalSelectedPark] = useState(null);
  const [attachmentName, setAttachmentName] = useState(""); // For displaying selected file name

  // Determine which selectedPark to use (prop or local)
  const selectedPark = propSelectedPark !== undefined ? propSelectedPark : localSelectedPark;
  
  // Determine which setSelectedPark to use (prop or local)
  const setSelectedPark = propSetSelectedPark || setLocalSelectedPark;

  // Fetch agreements when selectedPark changes
  useEffect(() => {
    const fetchAgreements = async () => {
      if (!selectedPark?.id) {
        setAgreementsList([]);
        setAgreementId('');
        return;
      }
      
      try {
        const params = { 
          type: 'Land Leased',
          park_id: selectedPark.id 
        };
        
        const agreData = await GetDataFromApiWithParams(`parks/getAgreementByType`, params);
        
        console.log("Agreements data:", agreData);
        const agreDataArray = Array.isArray(agreData) ? agreData : 
                    (agreData && agreData.data && Array.isArray(agreData.data)) ? agreData.data :
                    (agreData && typeof agreData === 'object') ? [agreData] : 
                    [];
          
        setAgreementsList(agreDataArray);
      } catch (error) {
        console.error("Error fetching agreements:", error);
        setAgreementsList([]);
        setResponseMessage("Error fetching agreements");
        setMessageType("error");
      }
    };
    
    fetchAgreements();
  }, [selectedPark, setAgreementId]);

  const handleAgreementInfoSearch = async () => {
    try {
      setResponseMessage("");

      if (!agreementId) {
        setResponseMessage("Please select an Agreement");
        setMessageType("error");
        return;
      }

      const result = await GetDataFromApiWithParams(`agreements/${agreementId}`, null);

      if (result && result.length) {
        const data = result[0];
        setInfoFromAgreement({
          titleDeedNo: agreementId || "",
          park: data?.park_name || "",
          upin: data?.upin || "",
          investor: data?.company_name || "",
          tin_no: data?.tin_no || "",
        });
      } else {
        setResponseMessage("Agreement not found");
        setMessageType("error");
        setInfoFromAgreement(initialSearchResult);
      }
    } catch (e) {
      console.error("Search error:", e);
      setResponseMessage("An error occurred while searching for the agreement");
      setMessageType("error");
      setInfoFromAgreement(initialSearchResult);
    }
  };

  const handleParkSelection = (park) => {
    setSelectedPark(park);
    setAgreementId('');
    setInfoFromAgreement(initialSearchResult);
    console.log("Selected park:", park);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRequestFormAttachment(file);
      setAttachmentName(file.name);
    }
  };

  return (
    <>
      <Grid container spacing={2} paddingLeft={6} paddingRight={6} sx={{ mt: 3 }}>
        {/* Row 1: Request Date, Type of Request, and Attachment */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Request Date"
              type="date"
              value={requestDate}
              onChange={e => setRequestDate(e.target.value)}
              fullWidth={true}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true} required>
              <InputLabel>Type of Request</InputLabel>
              <Select value={requestType} onChange={e => setRequestType(e.target.value)} required>
                {requestTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Attachment Button */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={requestFormAttachment ? <CheckCircle /> : <AttachFile />}
                sx={{ 
                  height: '56px',
                  backgroundColor: requestFormAttachment ? '#e8f5e8' : 'transparent',
                  borderColor: requestFormAttachment ? '#4caf50' : 'rgba(0, 0, 0, 0.23)',
                  color: requestFormAttachment ? '#2e7d32' : 'inherit',
                  '&:hover': {
                    backgroundColor: requestFormAttachment ? '#d0ebd0' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
                fullWidth
              >
                {requestFormAttachment ? ' Request Form' : 'Attach Request Form'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
              </Button>
              {attachmentName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <CheckCircle sx={{ fontSize: '1rem', color: '#2e7d32' }} />
                  <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                    {attachmentName}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Row 2: Park, Agreement, and Find Button */}
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} md={4}>
            <ParkList 
              selectedPark={selectedPark} 
              setSelectedPark={handleParkSelection} 
              required
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={!selectedPark?.id} required>
              <InputLabel sx={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '0.8rem' }}>
                Select Agreement
              </InputLabel>
              <Select
                value={agreementId || ''}
                onChange={(e) => setAgreementId(e.target.value)}
                label="Select Agreement"
                sx={{
                  fontFamily: '"Times New Roman", Times, serif',
                  fontSize: '0.8rem',
                  height: '40px',
                  '& .MuiSelect-select': { padding: '6px 10px' }
                }}
                required
              >
                <MenuItem value="">None</MenuItem>
                {agreementsList?.map((agreement) => (
                  <MenuItem key={agreement.id} value={agreement.id}>
                    {agreement?.agreement_no || agreement?.name || agreement?.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button 
                variant="outlined" 
                endIcon={<Search />} 
                onClick={handleAgreementInfoSearch}
                disabled={!agreementId}
                sx={{ height: '40px' }}
              >
                Find
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Row 3: UPIN, Investor, Title Deed No, Park */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField label="UPIN" value={infoFromAgreement.upin} fullWidth={true} disabled required />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Investor" value={infoFromAgreement.investor} fullWidth={true} disabled required />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Title Deed No" value={infoFromAgreement.titleDeedNo} fullWidth={true} disabled required />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Park" value={infoFromAgreement.park} fullWidth={true} disabled required />
          </Grid>
        </Grid>

        {/* Row 4: Consultant and Contractor */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <ConsultantList selectedConsultant={consultant} setSelectedConsultant={setConsultant} required />
            <Link to="/registerConsultant" style={{ marginLeft: "10px" }}>
              Register Consultant
            </Link>
          </Grid>
          <Grid item xs={12} md={6}>
            <ContractorList selectedContractor={contractor} setSelectedContractor={setContractor} required />
            <Link to="/registerContractor" style={{ marginLeft: "10px" }}>
              Register Contractor
            </Link>
          </Grid>
        </Grid>
      </Grid>
      
      {responseMessage && (
        <IPDCToastMessageResult message={responseMessage} type={messageType} />
      )}
    </>
  );
}

export default GeneralInfoTab;